import { useMemo } from "react";

import Beacon from "@gx/dls/beacons/Beacon";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, { AlignItems, JustifyContent } from "@gx/dls/containers/FlexWrapper";
import Text, { TextSize, TextVariant, TextWeight } from "@gx/dls/text/Text";
import { SourceStatus, SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";

import { SourceCardLoadingStatus } from "@/pages/sources/components/card/SourceCardLoading";
import SourceIcon from "@/pages/sources/components/SourceIcon";
import {
  SOURCE_STATUS_TO_BEACON_VARIANT_MAP,
  SOURCE_STATUS_TO_TEXT_MAP,
  SOURCE_STATUS_TO_TEXT_VARIANT_MAP,
} from "@/pages/sources/constants";

interface SourceCardHeaderProps {
  name: SourceResource["name"];
  sourceType: SourceResource["sourceType"];
  status: SourceResource["sourceStatus"];
  isStatusLoading: boolean;
  isDeleted: boolean;
}

const SourceCardHeader = ({
  name,
  sourceType,
  status,
  isStatusLoading,
  isDeleted,
}: SourceCardHeaderProps) => {
  const sourceStatus = useMemo(() => {
    return status ?? SourceStatus.UNSPECIFIED;
  }, [status]);

  const renderStatus = () => {
    if (isDeleted) {
      return (
        <FlexWrapper gap={6} alignItems={AlignItems.CENTER}>
          <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM} variant={TextVariant.ERROR}>
            Deleted
          </Text>
        </FlexWrapper>
      );
    }

    return (
      <FlexWrapper gap={6} alignItems={AlignItems.CENTER}>
        <Beacon variant={SOURCE_STATUS_TO_BEACON_VARIANT_MAP[sourceStatus]} isPulse />
        <Text
          size={TextSize.BODY_SM}
          weight={TextWeight.MEDIUM}
          variant={SOURCE_STATUS_TO_TEXT_VARIANT_MAP[sourceStatus]}
        >
          {SOURCE_STATUS_TO_TEXT_MAP[sourceStatus]}
        </Text>
      </FlexWrapper>
    );
  };

  return (
    <FlexWrapper
      justifyContent={JustifyContent.SPACE_BETWEEN}
      alignItems={AlignItems.CENTER}
      gap={2}
      fillWidth
    >
      <FlexWrapper gap={8} alignItems={AlignItems.CENTER} overflow="hidden">
        <FlexItem shrink={0}>
          <SourceIcon sourceType={sourceType ?? SourceType.UNSPECIFIED} />
        </FlexItem>
        <Text
          weight={TextWeight.MEDIUM}
          isEllipsis
          variant={isDeleted ? TextVariant.ERROR : TextVariant.PRIMARY}
        >
          {name}
        </Text>
      </FlexWrapper>
      {isStatusLoading ? <SourceCardLoadingStatus /> : renderStatus()}
    </FlexWrapper>
  );
};

export default SourceCardHeader;
